import React, { useMemo, useState } from "react";
import {
  GoogleMap,
  Marker,
  Polygon,
  Circle,
  InfoWindow,
} from "@react-google-maps/api";

// Predefined map styles
const MAP_STYLES = {
  lightGrey: [
    {
      featureType: "all",
      elementType: "geometry",
      stylers: [{ lightness: 40 }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ lightness: 20 }],
    },
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ],
  customGrey: [
    { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
    {
      featureType: "administrative.land_parcel",
      elementType: "labels.text.fill",
      stylers: [{ color: "#bdbdbd" }],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [{ color: "#eeeeee" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text.fill",
      stylers: [{ color: "#757575" }],
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{ color: "#e5e5e5" }],
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [{ color: "#9e9e9e" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#ffffff" }],
    },
    {
      featureType: "road.arterial",
      elementType: "labels.text.fill",
      stylers: [{ color: "#757575" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#dadada" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.fill",
      stylers: [{ color: "#616161" }],
    },
    {
      featureType: "road.local",
      elementType: "labels.text.fill",
      stylers: [{ color: "#9e9e9e" }],
    },
    {
      featureType: "transit.line",
      elementType: "geometry",
      stylers: [{ color: "#e5e5e5" }],
    },
    {
      featureType: "transit.station",
      elementType: "geometry",
      stylers: [{ color: "#eeeeee" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#c9c9c9" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#9e9e9e" }],
    },
  ],
};

const getMarkerIcons = () => ({
  blue: {
    url: `data:image/svg+xml;base64,${btoa(`
      <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='#3b82f6' stroke='white' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z'/>
        <circle cx='12' cy='10' r='3' fill='white'/>
      </svg>
    `)}`,
  },

  black: {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: "#000000",
    fillOpacity: 1,
    strokeWeight: 0,
  },
});

// Helper function to calculate polygon center
const getPolygonCenter = (paths) => {
  if (!paths || paths.length === 0) return null;

  const sumLat = paths.reduce((sum, point) => sum + point.lat, 0);
  const sumLng = paths.reduce((sum, point) => sum + point.lng, 0);

  return {
    lat: sumLat / paths.length,
    lng: sumLng / paths.length,
  };
};

// Helper function to determine quadrant label based on position
const getQuadrantLabel = (polygonCenter, overallCenter) => {
  if (!polygonCenter || !overallCenter) return "";

  const isNorth = polygonCenter.lat >= overallCenter.lat;
  const isEast = polygonCenter.lng >= overallCenter.lng;

  if (isNorth && !isEast) return "NW";
  if (isNorth && isEast) return "NE";
  if (!isNorth && !isEast) return "SW";
  if (!isNorth && isEast) return "SE";

  return "";
};

const GoogleMaps = ({
  center,
  polygons = [],
  markers = [],
  circles = [],
  areaOutline = null,
  onMarkerClick,
  onMapLoad,
  options = {},
  fullScreen = false,
}) => {
  const {
    view = "hybrid",
    zoom = 19,
    mapStyle = "default",
    showQuadrants = false,
    ...googleMapsOptions
  } = options;

  // popup = { position, data }
  const [popup, setPopup] = useState(null);

  const isValidCoord = (v) => Number.isFinite(Number(v)) && !isNaN(Number(v));

  // Keep center stable across re-renders
  const mapCenter = useMemo(
    () => ({
      lat: center.lat,
      lng: center.lon !== undefined ? center.lon : center.lng,
    }),
    [center.lat, center.lon, center.lng]
  );

  // Memoize marker icons
  const MARKER_ICONS = useMemo(() => {
    if (typeof window !== "undefined" && window.google?.maps) {
      return getMarkerIcons();
    }
    return {};
  }, []);

  // Calculate the overall center of all polygons for quadrant detection
  const polygonsCenter = useMemo(() => {
    if (polygons.length === 0) return mapCenter;

    const allPoints = polygons.flatMap((p) => p.paths || []);
    if (allPoints.length === 0) return mapCenter;

    const sumLat = allPoints.reduce((sum, point) => sum + point.lat, 0);
    const sumLng = allPoints.reduce((sum, point) => sum + point.lng, 0);

    return {
      lat: sumLat / allPoints.length,
      lng: sumLng / allPoints.length,
    };
  }, [polygons, mapCenter]);

  // Calculate quadrants
  const quadrants = useMemo(() => {
    if (!areaOutline || !showQuadrants) return null;

    const bounds = areaOutline.reduce(
      (acc, { lat, lng }) => ({
        north: Math.max(acc.north, lat),
        south: Math.min(acc.south, lat),
        east: Math.max(acc.east, lng),
        west: Math.min(acc.west, lng),
      }),
      { north: -90, south: 90, east: -180, west: 180 }
    );

    const centerLat = (bounds.north + bounds.south) / 2;
    const centerLng = (bounds.east + bounds.west) / 2;

    return {
      NW: [
        { lat: bounds.north, lng: bounds.west },
        { lat: bounds.north, lng: centerLng },
        { lat: centerLat, lng: centerLng },
        { lat: centerLat, lng: bounds.west },
      ],
      NE: [
        { lat: bounds.north, lng: centerLng },
        { lat: bounds.north, lng: bounds.east },
        { lat: centerLat, lng: bounds.east },
        { lat: centerLat, lng: centerLng },
      ],
      SW: [
        { lat: centerLat, lng: bounds.west },
        { lat: centerLat, lng: centerLng },
        { lat: bounds.south, lng: centerLng },
        { lat: bounds.south, lng: bounds.west },
      ],
      SE: [
        { lat: centerLat, lng: centerLng },
        { lat: centerLat, lng: bounds.east },
        { lat: bounds.south, lng: bounds.east },
        { lat: bounds.south, lng: centerLng },
      ],
    };
  }, [areaOutline, showQuadrants]);

  const mapOptions = {
    mapTypeId: view,
    disableDefaultUI: false,
    ...(MAP_STYLES[mapStyle] && { styles: MAP_STYLES[mapStyle] }),
    ...googleMapsOptions,
  };

  // Get marker icon based on type or iconType
  const getMarkerIcon = (marker) => {
    if (marker.icon) return marker.icon;

    if (marker.iconType && MARKER_ICONS[marker.iconType]) {
      return MARKER_ICONS[marker.iconType];
    }

    if (marker.type === "center" && areaOutline) {
      return MARKER_ICONS.black;
    }

    if (marker.type === "custom") {
      return MARKER_ICONS.blue;
    }

    return undefined;
  };

  const handleMarkerClick = (marker) => {
    if (!isValidCoord(marker.lat) && !isValidCoord(marker.position?.lat))
      return;

    const position = marker.position || {
      lat: Number(marker.lat),
      lng: Number(marker.lon || marker.lng),
    };

    setPopup({
      position,
      data: marker.data || [],
    });

    onMarkerClick?.(marker.data);
  };

  // Filter popup data safely
  const filteredPopupData = popup?.data?.filter(
    (item) => item && String(item).trim() !== ""
  );

  return (
    <div
      className={
        fullScreen
          ? "w-full h-full"
          : "w-full max-w-[800px] mx-auto h-[400px] my-6"
      }
    >
      <GoogleMap
        center={mapCenter}
        zoom={zoom}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        options={mapOptions}
        onClick={() => setPopup(null)}
        onLoad={onMapLoad}
      >
        {/* All markers */}
        {markers
          .filter((marker) => {
            if (marker.position) return true;
            return (
              isValidCoord(marker.lat) && isValidCoord(marker.lon || marker.lng)
            );
          })
          .map((marker, index) => (
            <Marker
              key={marker.id || index}
              position={
                marker.position || {
                  lat: Number(marker.lat),
                  lng: Number(marker.lon || marker.lng),
                }
              }
              icon={getMarkerIcon(marker)}
              label={marker.label || undefined}
              title={marker.title || undefined}
              clickable={marker.clickable !== false}
              onClick={() => handleMarkerClick(marker)}
            />
          ))}

        {/* Popup */}
        {popup && filteredPopupData?.length > 0 && (
          <InfoWindow
            position={popup.position}
            onCloseClick={() => setPopup(null)}
          >
            <div className="space-y-2">
              {filteredPopupData.map((item, i) => (
                <p className="text-xs" key={i}>
                  {item}
                </p>
              ))}
            </div>
          </InfoWindow>
        )}

        {/* Circles (coverage zones, radius areas) */}
        {circles.map((circle, index) => (
          <Circle
            key={circle.id || index}
            center={circle.center}
            radius={circle.radius}
            options={{
              fillColor: circle.fillColor || circle.color || "#999",
              fillOpacity: circle.fillOpacity ?? 0.12,
              strokeColor: circle.strokeColor || circle.color || "#999",
              strokeWeight: circle.strokeWeight ?? 1.5,
              clickable: circle.clickable ?? false,
            }}
          />
        ))}

        {/* Quadrants with labels */}
        {quadrants &&
          Object.entries(quadrants).map(([name, paths]) => (
            <React.Fragment key={name}>
              <Polygon
                paths={paths}
                options={{
                  fillColor: "#000000",
                  fillOpacity: 0.1,
                  strokeColor: "#0000002F",
                  strokeOpacity: 0.7,
                  strokeWeight: 1,
                }}
              />
              <Marker
                position={{
                  lat: (paths[0].lat + paths[2].lat) / 2,
                  lng: (paths[0].lng + paths[2].lng) / 2,
                }}
                icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 0 }}
                label={{
                  text: name,
                  color: "#000000",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              />
            </React.Fragment>
          ))}

        {/* Area outline */}
        {areaOutline && (
          <Polygon
            paths={areaOutline}
            options={{
              fillOpacity: 0.1,
              strokeColor: "#000000",
              strokeOpacity: 0.8,
              strokeWeight: 1,
            }}
          />
        )}

        {/* All polygons with auto-detected quadrant labels */}
        {polygons.map((polygon, index) => {
          const centerPosition = getPolygonCenter(polygon.paths);
          const autoLabel = centerPosition
            ? getQuadrantLabel(centerPosition, polygonsCenter)
            : "";
          const label = polygon.label !== undefined ? polygon.label : autoLabel;

          return (
            <React.Fragment key={polygon.id || index}>
              <Polygon
                paths={polygon.paths}
                options={
                  polygon.options || {
                    fillOpacity: 0.4,
                    strokeColor: "#d35400",
                    strokeOpacity: 0.8,
                    strokeWeight: 3,
                  }
                }
              />
              {centerPosition && label && (
                <Marker
                  position={centerPosition}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 0,
                  }}
                  label={{
                    text: label,
                    color: "#000000",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </GoogleMap>
    </div>
  );
};

export default GoogleMaps;
